import * as React from 'react';
import { useMemo, useState } from 'react';
import { Plus, Sparkles, Trash2 } from 'lucide-react';

import { ConfigEditorModal, ConfigSection, ConfigSummaryTile } from './config-editor-modal';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ProvenanceChip } from '@/components/ui/provenance-chip';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api/client';
import {
  useAddRubricCriterionMutation,
  useDeleteRubricCriterionMutation,
  usePatchRubricCriterionMutation,
  usePatchRubricThresholdsMutation,
  useReadinessRubricQuery,
  type ReadinessRubric,
  type RubricCriterion,
  type RubricProvenance,
  type RubricScoreType,
} from '@/lib/api/readiness-rubric';
import { useAdminProposalsQuery } from '@/lib/api/admin-proposals';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { formatDate, titleCase } from '@/lib/utils/format';

/**
 * T-0114 / E08-F01 — Rubric editor modal.
 *
 * Visual contract:
 *   - `design_handoff_caliber_v2/prototype/modals-round4b.js → openRubricEditor`
 *   - README §4 modal table row `data-open-rubric`
 *
 * Behavior:
 *   - Loads the live rubric for the engagement via `useReadinessRubricQuery`.
 *   - Groups criteria by category; each row shows name + weight (%)+ score
 *     type + provenance.
 *   - Weight edits are buffered in a `drafts` map keyed by criterion id;
 *     dirty state is computed against the buffered map vs. the live
 *     weights pulled from the server.
 *   - "Save & apply" iterates the dirty criterion ids and patches each in
 *     turn through the T-0082 backend, with a structured override blob
 *     (`tag: 'weight_change'`, `rationale: '<editor-supplied note>'`).
 *   - Category-weight totals and the global 100% invariant are displayed
 *     live so consultants see drift before saving. The TA spec leaves
 *     server-side validation as the source of truth; the client just
 *     surfaces hints.
 *
 * Backend gaps (flagged in PR body):
 *   - The T-0082 PATCH endpoint accepts only `weight`, `pass_threshold`,
 *     `override`. The prototype shows an "Add criterion" / "Remove
 *     criterion" action — not exposed by the backend (no POST/DELETE
 *     criterion endpoints). The modal disables those affordances with
 *     a tooltip and notes the gap in the PR.
 *   - Threshold edits (`go_threshold`, `conditional_threshold`) are not
 *     PATCH-able post-creation; the rubric thresholds field is set at
 *     POST .../rubric time. The modal renders them read-only with a
 *     "managed via methodology config" hint.
 *   - AI-proposed variant accept/reject flow uses the rubric POST with
 *     `accept_ai_proposal_id`; no dedicated proposal endpoint is exposed
 *     to the client yet. The card surfaces the affordance with a "TODO"
 *     tooltip until the proposal-list backend lands.
 */

export interface RubricEditorModalProps {
  engagementId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CriterionDraft {
  weight: number;
  rationale: string;
}

export function RubricEditorModal({ engagementId, open, onOpenChange }: RubricEditorModalProps) {
  const query = useReadinessRubricQuery(open ? engagementId : undefined);
  const rubric = query.data;

  // T-0122 / T-0126: admin proposals + threshold + add/remove criterion
  // endpoints. The Accept/Reject panel was empty in v1 because no read
  // surface existed; with T-0122 it's a single firm-admin GET. Non-admin
  // callers see a 403 here — we surface it as a soft empty state.
  const adminProposals = useAdminProposalsQuery(
    { type: 'rubric', status: 'pending' },
    Boolean(open),
  );

  // Baseline weight map — re-seeded whenever the server data changes.
  const baselineWeights = useMemo<Record<string, number>>(() => {
    if (!rubric) return {};
    const out: Record<string, number> = {};
    for (const c of rubric.criteria) out[c.id] = c.weight;
    return out;
  }, [rubric]);

  const [drafts, setDrafts] = useState<Record<string, CriterionDraft>>({});
  const [thresholdDraft, setThresholdDraft] = useState<{
    go_threshold?: number;
    conditional_threshold?: number;
  }>({});
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());
  const [showAddRow, setShowAddRow] = useState(false);
  const [addDraft, setAddDraft] = useState<{
    category: string;
    name: string;
    weight: string;
    score_type: RubricScoreType;
    pass_threshold: string;
  }>({
    category: '',
    name: '',
    weight: '',
    score_type: 'numeric_0_10',
    pass_threshold: '',
  });

  // Reset drafts when the modal closes (so reopening shows the live state).
  // We don't auto-reset on rubric refetch because the consultant may be
  // mid-edit; the dirty-state guard handles close.
  const isDirty =
    Object.keys(drafts).length > 0 ||
    Object.keys(thresholdDraft).length > 0 ||
    pendingDeletes.size > 0 ||
    showAddRow;

  const mutation = usePatchRubricCriterionMutation(engagementId);
  const addMutation = useAddRubricCriterionMutation(engagementId);
  const deleteMutation = useDeleteRubricCriterionMutation(engagementId);
  const thresholdMutation = usePatchRubricThresholdsMutation(engagementId);
  const [saveError, setSaveError] = useState<unknown>(null);

  const saving =
    mutation.isPending ||
    addMutation.isPending ||
    deleteMutation.isPending ||
    thresholdMutation.isPending;

  const handleSave = async ({ closeOnSuccess }: { closeOnSuccess: boolean }) => {
    if (!rubric) return;
    setSaveError(null);
    try {
      // 1) Threshold PATCH first — the new endpoint accepts partial body.
      if (Object.keys(thresholdDraft).length > 0) {
        await thresholdMutation.mutateAsync(thresholdDraft);
        setThresholdDraft({});
      }
      // 2) Soft-deletes — order before weight edits so the rebalance
      //    runs against the smaller criterion set.
      for (const criterionId of pendingDeletes) {
        await deleteMutation.mutateAsync({ criterionId });
      }
      // 3) Add-criterion if the consultant filled the form.
      if (showAddRow) {
        const w = Number(addDraft.weight);
        if (!Number.isFinite(w) || w <= 0 || w > 1) {
          throw new ApiError(
            400,
            'validation',
            'New criterion weight must be a fraction between 0 and 1 (e.g. 0.05 for 5%).',
            '',
          );
        }
        const passThreshold =
          addDraft.pass_threshold.trim().length === 0 ? null : Number(addDraft.pass_threshold);
        await addMutation.mutateAsync({
          body: {
            category: addDraft.category.trim(),
            name: addDraft.name.trim(),
            weight: w,
            score_type: addDraft.score_type,
            pass_threshold: passThreshold,
          },
        });
        setShowAddRow(false);
        setAddDraft({
          category: '',
          name: '',
          weight: '',
          score_type: 'numeric_0_10',
          pass_threshold: '',
        });
      }
      // 4) Sequential weight saves with rationale.
      for (const [criterionId, draft] of Object.entries(drafts)) {
        const live = baselineWeights[criterionId];
        if (live === undefined || draft.weight === live) continue;
        const rationale = draft.rationale?.trim();
        if (!rationale) {
          throw new ApiError(
            400,
            'validation',
            'Weight changes require a one-line rationale per criterion. The audit trail captures it alongside the change.',
            '',
          );
        }
        await mutation.mutateAsync({
          criterionId,
          body: {
            weight: draft.weight,
            override: { tag: 'weight_change', rationale },
          },
        });
      }
      setDrafts({});
      setPendingDeletes(new Set());
      if (closeOnSuccess) onOpenChange(false);
    } catch (err) {
      setSaveError(err);
    }
  };

  const toggleDelete = (criterionId: string) => {
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      if (next.has(criterionId)) next.delete(criterionId);
      else next.add(criterionId);
      return next;
    });
  };

  const updateDraft = (criterionId: string, updates: Partial<CriterionDraft>) => {
    setDrafts((prev) => {
      const base: CriterionDraft = prev[criterionId] ?? {
        weight: baselineWeights[criterionId] ?? 0,
        rationale: '',
      };
      const next: CriterionDraft = { ...base, ...updates };
      // If the weight is identical to baseline and rationale is empty,
      // drop the draft entry entirely so dirty-state clears.
      const live = baselineWeights[criterionId] ?? 0;
      if (next.weight === live && next.rationale.trim().length === 0) {
        const copy = { ...prev };
        delete copy[criterionId];
        return copy;
      }
      return { ...prev, [criterionId]: next };
    });
  };

  return (
    <ConfigEditorModal
      open={open}
      onOpenChange={onOpenChange}
      bread="Go-Live readiness"
      title="Readiness rubric configuration"
      sub="Criteria across categories. Weights sum to 100%. Go / Conditional / No-Go thresholds set per engagement; AI may propose adjustments from cutover-planning documents."
      isDirty={isDirty}
      onSave={handleSave}
      saving={saving}
      saveError={saveError ? <ErrorMessage error={saveError} /> : undefined}
      foot="Each weight edit is captured with tag + rationale via the override primitive. AI re-evaluation will re-run on save."
      summary={
        query.isLoading ? (
          <div className="col-span-full flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading rubric…
          </div>
        ) : rubric ? (
          <RubricSummary rubric={rubric} drafts={drafts} />
        ) : null
      }
    >
      {query.isLoading ? (
        <div className="rounded-card border border-ink-100 bg-paper-0 p-card-pad text-body-sm text-ink-500">
          Loading rubric…
        </div>
      ) : query.error ? (
        <ErrorMessage error={query.error} />
      ) : rubric ? (
        <RubricBody
          rubric={rubric}
          drafts={drafts}
          updateDraft={updateDraft}
          thresholdDraft={thresholdDraft}
          setThresholdDraft={setThresholdDraft}
          pendingDeletes={pendingDeletes}
          toggleDelete={toggleDelete}
          showAddRow={showAddRow}
          setShowAddRow={setShowAddRow}
          addDraft={addDraft}
          setAddDraft={setAddDraft}
          adminProposalCount={adminProposals.data?.items.length ?? 0}
          adminProposalLoading={adminProposals.isLoading}
          adminProposalError={adminProposals.error}
          adminProposalItems={adminProposals.data?.items ?? []}
        />
      ) : null}
    </ConfigEditorModal>
  );
}

function RubricSummary({
  rubric,
  drafts,
}: {
  rubric: ReadinessRubric;
  drafts: Record<string, CriterionDraft>;
}) {
  const effective = rubric.criteria.map((c) => ({
    id: c.id,
    weight: drafts[c.id]?.weight ?? c.weight,
  }));
  const totalW = effective.reduce((a, c) => a + c.weight, 0);
  const sumDelta = Math.abs(totalW - 1);
  const tone = sumDelta < 0.005 ? 'ok' : sumDelta < 0.02 ? 'warn' : 'crit';
  return (
    <>
      <ConfigSummaryTile
        label="Criteria"
        value={rubric.criteria.length}
        sub={`across ${new Set(rubric.criteria.map((c) => c.category)).size} categories`}
      />
      <ConfigSummaryTile
        label="Weight sum"
        value={`${(totalW * 100).toFixed(0)}%`}
        sub={sumDelta < 0.005 ? 'normalized' : 'must equal 100%'}
        tone={tone}
      />
      <ConfigSummaryTile
        label="Go threshold"
        value={`≥ ${(rubric.go_threshold / 10).toFixed(1)}`}
        sub="weighted total 0–10"
        tone="ok"
      />
      <ConfigSummaryTile
        label="Conditional"
        value={`≥ ${(rubric.conditional_threshold / 10).toFixed(1)}`}
        sub="below: No-Go"
        tone="warn"
      />
    </>
  );
}

function RubricBody({
  rubric,
  drafts,
  updateDraft,
  thresholdDraft,
  setThresholdDraft,
  pendingDeletes,
  toggleDelete,
  showAddRow,
  setShowAddRow,
  addDraft,
  setAddDraft,
  adminProposalCount,
  adminProposalLoading,
  adminProposalError,
  adminProposalItems,
}: {
  rubric: ReadinessRubric;
  drafts: Record<string, CriterionDraft>;
  updateDraft: (criterionId: string, updates: Partial<CriterionDraft>) => void;
  thresholdDraft: { go_threshold?: number; conditional_threshold?: number };
  setThresholdDraft: React.Dispatch<
    React.SetStateAction<{ go_threshold?: number; conditional_threshold?: number }>
  >;
  pendingDeletes: Set<string>;
  toggleDelete: (criterionId: string) => void;
  showAddRow: boolean;
  setShowAddRow: React.Dispatch<React.SetStateAction<boolean>>;
  addDraft: {
    category: string;
    name: string;
    weight: string;
    score_type: RubricScoreType;
    pass_threshold: string;
  };
  setAddDraft: React.Dispatch<
    React.SetStateAction<{
      category: string;
      name: string;
      weight: string;
      score_type: RubricScoreType;
      pass_threshold: string;
    }>
  >;
  adminProposalCount: number;
  adminProposalLoading: boolean;
  adminProposalError: unknown;
  adminProposalItems: ReadonlyArray<{
    id: string;
    confidence: number;
    proposal_type: string;
    created_at: string;
  }>;
}) {
  // Group criteria by category, preserving display_order within each group.
  const grouped = useMemo(() => {
    const map = new Map<string, RubricCriterion[]>();
    for (const c of rubric.criteria) {
      const list = map.get(c.category) ?? [];
      list.push(c);
      map.set(c.category, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.display_order - b.display_order);
    return Array.from(map.entries());
  }, [rubric]);

  const goEffective = thresholdDraft.go_threshold ?? rubric.go_threshold;
  const condEffective = thresholdDraft.conditional_threshold ?? rubric.conditional_threshold;

  return (
    <>
      <ConfigSection
        heading="Thresholds"
        meta={`Per-engagement · ${rubric.thresholds_source === 'engagement_override' ? 'overridden' : 'methodology default'}`}
      >
        <div className="grid gap-4 px-4 py-4 sm:grid-cols-3">
          <ThresholdNumberInput
            label="Go threshold"
            value={(goEffective / 10).toFixed(1)}
            help="Default 7.5 — from PMI 8-step template"
            onChange={(next) => {
              const raw = Number(next);
              if (!Number.isFinite(raw)) return;
              setThresholdDraft((prev) => ({ ...prev, go_threshold: Math.round(raw * 10) }));
            }}
          />
          <ThresholdNumberInput
            label="Conditional threshold"
            value={(condEffective / 10).toFixed(1)}
            help="Default 5.5 — below = No-Go"
            onChange={(next) => {
              const raw = Number(next);
              if (!Number.isFinite(raw)) return;
              setThresholdDraft((prev) => ({
                ...prev,
                conditional_threshold: Math.round(raw * 10),
              }));
            }}
          />
          <ThresholdNumberInput
            label="Weight policy"
            value="Strict 100%"
            help="Recommended: strict"
            disabled
          />
        </div>
        <div className="bg-paper-50 px-4 py-2 text-meta text-ink-500">
          Thresholds saved here apply to this engagement only and replace the firm defaults for it.
        </div>
      </ConfigSection>

      {grouped.map(([category, criteria]) => {
        const effective = criteria.map((c) => ({
          ...c,
          effective: drafts[c.id]?.weight ?? c.weight,
        }));
        const catWeight = effective.reduce((a, c) => a + c.effective, 0);
        return (
          <ConfigSection
            key={category}
            heading={category}
            meta={`${criteria.length} criteria · category weight ${(catWeight * 100).toFixed(0)}%`}
          >
            {effective.map((c) => (
              <CriterionRow
                key={c.id}
                criterion={c}
                draft={drafts[c.id]}
                onUpdate={(updates) => updateDraft(c.id, updates)}
                isPendingDelete={pendingDeletes.has(c.id)}
                onToggleDelete={() => toggleDelete(c.id)}
              />
            ))}
          </ConfigSection>
        );
      })}

      <ConfigSection
        heading="Add criterion"
        meta="Adds to the active rubric. Weight is a fraction (0 to 1)."
        actions={
          !showAddRow ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowAddRow(true)}>
              <Plus className="h-3 w-3" aria-hidden="true" />
              Add criterion
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowAddRow(false)}>
              Cancel
            </Button>
          )
        }
      >
        {showAddRow ? (
          <div className="grid gap-3 px-4 py-3 sm:grid-cols-[1fr,1fr,96px,140px,120px]">
            <div>
              <label className="block text-meta text-ink-500">Category</label>
              <input
                type="text"
                value={addDraft.category}
                onChange={(e) => setAddDraft((p) => ({ ...p, category: e.target.value }))}
                className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
              />
            </div>
            <div>
              <label className="block text-meta text-ink-500">Name</label>
              <input
                type="text"
                value={addDraft.name}
                onChange={(e) => setAddDraft((p) => ({ ...p, name: e.target.value }))}
                className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
              />
            </div>
            <div>
              <label className="block text-meta text-ink-500">Weight</label>
              <input
                type="number"
                step="0.01"
                min={0}
                max={1}
                value={addDraft.weight}
                onChange={(e) => setAddDraft((p) => ({ ...p, weight: e.target.value }))}
                placeholder="0.05"
                className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 font-mono text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
              />
            </div>
            <div>
              <label className="block text-meta text-ink-500">Score type</label>
              <select
                value={addDraft.score_type}
                onChange={(e) =>
                  setAddDraft((p) => ({ ...p, score_type: e.target.value as RubricScoreType }))
                }
                className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-700 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
              >
                <option value="numeric_0_10">numeric 0..10</option>
                <option value="pass_fail">pass / fail</option>
                <option value="ryg">RYG</option>
              </select>
            </div>
            <div>
              <label className="block text-meta text-ink-500">Pass threshold</label>
              <input
                type="number"
                value={addDraft.pass_threshold}
                onChange={(e) => setAddDraft((p) => ({ ...p, pass_threshold: e.target.value }))}
                placeholder="optional"
                className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 font-mono text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
              />
            </div>
          </div>
        ) : (
          <div className="px-4 py-3 text-meta text-ink-500">
            Click "Add criterion" to append a new row. The rubric's weights must sum to 1.0 after
            save; rebalance existing weights inline before committing.
          </div>
        )}
      </ConfigSection>

      <ConfigSection
        tone="ai"
        heading={
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            AI proposals from cutover docs
          </span>
        }
        meta={
          adminProposalLoading
            ? 'Loading…'
            : adminProposalError
              ? 'Firm admins only.'
              : `${adminProposalCount} pending`
        }
      >
        {adminProposalLoading ? (
          <div className="px-4 py-4 text-meta text-ai-ink">Loading proposal queue…</div>
        ) : adminProposalError ? (
          <div className="px-4 py-4 text-meta text-ai-ink">
            Only firm admins can accept or reject these proposals. Ask a firm admin to review them.
          </div>
        ) : adminProposalItems.length === 0 ? (
          <div className="px-4 py-4 text-meta text-ai-ink">
            None pending — the next baseline re-evaluation will populate this card.
          </div>
        ) : (
          <ul className="divide-y divide-ai-surface-border">
            {adminProposalItems.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-baseline justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-body-sm font-medium text-ai-ink">
                    {titleCase(p.proposal_type)}
                  </div>
                  <div className="mt-0.5 text-meta text-ai-ink/70">
                    Proposed {formatDate(p.created_at)}
                  </div>
                </div>
                <AiConfidenceChip value={p.confidence} />
              </li>
            ))}
          </ul>
        )}
      </ConfigSection>
    </>
  );
}

function ThresholdNumberInput({
  label,
  value,
  help,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  help: string;
  disabled?: boolean;
  onChange?: (next: string) => void;
}) {
  return (
    <div>
      <label className="text-meta font-medium text-ink-500">{label}</label>
      <input
        type={disabled ? 'text' : 'number'}
        step="0.1"
        value={value}
        readOnly={disabled || !onChange}
        disabled={disabled}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        className={`mt-1 block h-input-h w-full rounded-card border border-ink-200 px-2 font-mono text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600 ${
          disabled || !onChange ? 'cursor-not-allowed bg-paper-100 text-ink-700' : 'bg-paper-0'
        }`}
      />
      <div className="mt-1 text-meta text-ink-500">{help}</div>
    </div>
  );
}

function CriterionRow({
  criterion,
  draft,
  onUpdate,
  isPendingDelete,
  onToggleDelete,
}: {
  criterion: RubricCriterion & { effective: number };
  draft: CriterionDraft | undefined;
  onUpdate: (updates: Partial<CriterionDraft>) => void;
  isPendingDelete: boolean;
  onToggleDelete: () => void;
}) {
  const isDirty = draft !== undefined;
  const weightPct = Math.round(criterion.effective * 1000) / 10;
  return (
    <div
      className={`grid gap-3 px-4 py-3 sm:grid-cols-[1fr,96px,120px,120px,auto] ${isPendingDelete ? 'opacity-60' : ''}`}
    >
      <div className="min-w-0">
        <div className="text-body-sm font-medium text-ink-900">{criterion.name}</div>
        <div className="mt-0.5 font-mono text-meta text-ink-500">{criterion.id.slice(0, 8)}</div>
        {isDirty ? (
          <input
            type="text"
            placeholder="Rationale (required for save)"
            value={draft?.rationale ?? ''}
            onChange={(e) => onUpdate({ rationale: e.target.value })}
            className="mt-2 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
          />
        ) : null}
      </div>
      <div>
        <label className="block text-meta text-ink-500">Weight %</label>
        <input
          type="number"
          min={0}
          max={100}
          step={1}
          value={Number.isFinite(weightPct) ? weightPct : ''}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (!Number.isFinite(next)) return;
            onUpdate({ weight: Math.max(0, Math.min(1, next / 100)) });
          }}
          disabled={isPendingDelete}
          className="mt-1 h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 font-mono text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600 disabled:cursor-not-allowed disabled:bg-paper-100"
        />
      </div>
      <div>
        <label className="block text-meta text-ink-500">Score type</label>
        <div className="mt-1 inline-flex h-input-h items-center rounded-card border border-ink-200 bg-paper-100 px-2 font-mono text-meta text-ink-700">
          {criterion.score_type}
        </div>
      </div>
      <div>
        <label className="block text-meta text-ink-500">Pass threshold</label>
        <div className="mt-1 inline-flex h-input-h items-center rounded-card border border-ink-200 bg-paper-100 px-2 font-mono text-meta text-ink-700">
          {criterion.pass_threshold === null
            ? '—'
            : criterion.score_type === 'numeric_0_10'
              ? (criterion.pass_threshold / 10).toFixed(1)
              : String(criterion.pass_threshold)}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <ProvenanceChip origin={mapProvenance(criterion.provenance)} />
        {criterion.cross_module_sources.length > 0 ? (
          <span className="text-meta text-ink-500">
            {criterion.cross_module_sources.length} live source
            {criterion.cross_module_sources.length === 1 ? '' : 's'}
          </span>
        ) : null}
        <button
          type="button"
          onClick={onToggleDelete}
          title={
            isPendingDelete
              ? 'Undo soft-remove'
              : 'Soft-remove (preserves history; never hard-deletes)'
          }
          className="inline-flex h-7 items-center gap-1 rounded-card border border-ink-200 px-2 text-meta text-posture-critical hover:bg-posture-critical-tint focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
        >
          <Trash2 className="h-3 w-3" aria-hidden="true" />
          {isPendingDelete ? 'Will remove' : 'Soft-remove'}
        </button>
      </div>
    </div>
  );
}

function mapProvenance(p: RubricProvenance) {
  if (p === 'ai_proposed') return 'ai_proposed';
  if (p === 'consultant_confirmed') return 'ai_confirmed';
  return 'manual';
}
